import axios, { AxiosResponse } from 'axios';
import { Feeding } from 'views/production-systems/types/feeding.type';
import { PondState } from 'views/production-systems/types/pond.type';
import { Zone } from 'views/production-systems/types/zone.type';

import { CreateFeedingInput } from 'views/production-systems/features/farm-overview/types/create-feeding-input.type';
import { CreateStockingInput } from 'views/production-systems/features/farm-overview/types/create-stocking-input.type';
import { CreateHarvestInput } from 'views/production-systems/features/farm-overview/types/CreateHarvest.model';
import { Harvest, UpdateHarvest } from 'views/production-systems/types/harvest.type';

import { CreateSamplingInput } from 'views/production-systems/features/farm-overview/types/create-sampling-input.type';
import { CreateWaterQualityInput } from 'views/production-systems/features/farm-overview/types/create-water-quality-input.type';
import { WaterQualityType } from 'views/production-systems/types/water-quality-type.type';
import { WaterQuality } from 'views/production-systems/types/water-quality.type';

import { CompanyInfo } from 'views/production-systems/features/farm-overview/types/company-info.type';
import { CreateTransferInput } from 'views/production-systems/features/farm-overview/types/create-transfer-input.type';
import { CreateTreatmentInput } from 'views/production-systems/features/farm-overview/types/create-treatment-input.type';
import { RegistrationType } from 'views/production-systems/features/registration-log/types/registration-type.enum';
import { Registration } from 'views/production-systems/features/registration-log/types/registration.type';
import { UpdateFeedingInput } from 'views/production-systems/features/registration-log/types/update-feeding-input.type';
import { UpdateSamplingInput } from 'views/production-systems/features/registration-log/types/update-sampling-input.type';
import { UpdateStockingInput } from 'views/production-systems/features/registration-log/types/update-stocking-input.type';
import { UpdateTransferInput } from 'views/production-systems/features/registration-log/types/update-transfer-input.type';
import { UpdateTreatmentInput } from 'views/production-systems/features/registration-log/types/update-treatment-input.type';
import { UpdateWaterQualityInput } from 'views/production-systems/features/registration-log/types/update-water-quality-input.type';
import { Sampling } from 'views/production-systems/types/sampling.type';
import { NurseryList, Stocking } from 'views/production-systems/types/stocking.type';
import { Transfer } from 'views/production-systems/types/transfer.type';
import { Treatment } from 'views/production-systems/types/treatment.type';
import { Feed } from 'views/self-service/features/feed-catalog/types/feed.type';
import { FeederBrand } from 'views/self-service/types/feeder-brand.type';
import { UpdateAssignedFeedersInput } from 'views/production-systems/features/farm-setup/types/update-feeders-input.type';
import { FeederRegistationLog } from 'views/production-systems/features/farm-setup/types/feeder-registration.type';
import { AeratorRegistationLog } from 'views/production-systems/features/farm-setup/types/registration.aerator.type';
import { UpdateAssignedAeratorsInput } from 'views/production-systems/features/farm-setup/types/update-aerators-input.type';
import { Aerator } from 'views/production-systems/features/farm-setup/types/aerators.type';
import { FeedingStatus } from 'views/production-systems/features/farm-overview/types/feeding-status.type';
import { GetFeedingStatusInput } from 'views/production-systems/features/farm-overview/types/feeding-status-input.type';
import { CycleRegistrationExists } from 'views/production-systems/features/farm-overview/types/cycle-registration-exists.type';
import { RegistrationActionType } from 'views/production-systems/features/farm-overview/types/registration-action-type.enum';

export type FeedResponse = {};

// GET COMPANY INFO
// /ewosapi/v1/shrimps/CompanyInfo/{globalSiteId}
export const getCompanyInfo = async (globalSiteId: string): Promise<CompanyInfo[]> => {
  const res = await axios.get(`/ewosapi/v1/shrimps/CompanyInfo/${globalSiteId}`);
  return res.data;
};

// GET PONDS
// /ewosapi/v1/shrimps/ponds/{globalSiteId}/{date}
export const getPonds = async (globalSiteId: string, date?: string): Promise<PondState> => {
  const res = await axios.get<PondState>(`/ewosapi/v1/shrimps/ponds/${globalSiteId}/${date === '' ? 'latestStatus' : date}`);
  return res.data;
};

export const getZones = async (globalSiteId: string): Promise<Zone[]> => {
  const res = await axios.get(`/ewosapi/v1/shrimps/zones/${globalSiteId}`);
  return res.data;
};

export const getRegistrationLog = async (globalSiteId: string, fromDate: string, toDate: string): Promise<Registration[]> => {
  const res = await axios.get(`/ewosapi/v1/shrimps/registrationlog/${globalSiteId}/${fromDate}/${toDate}`);
  return res.data;
};

// POST REGISTRATION EXISTS
// /ewosapi/v1/shrimps/RegistrationLog/exists/{globalSiteId}/{actionTypeId}/{date}
export const postRegistrationExists = async (
  globalSiteId: string,
  actionTypeId: RegistrationActionType,
  date: string,
  cycleIds: number[]
): Promise<CycleRegistrationExists[]> => {
  const res = await axios.post<CycleRegistrationExists[]>(
    `/ewosapi/v1/shrimps/RegistrationLog/exists/${globalSiteId}/${actionTypeId}/${date}`,
    cycleIds
  );
  return res.data;
};

// DELETE REGISTRATION LOG
// /ewosapi/v1/shrimps/RegistrationLog/{globalSiteId}/{logId}
export const deleteRegistrationLog = async (globalSiteId: string, logId: number): Promise<AxiosResponse> => {
  return await axios.delete<AxiosResponse>(`/ewosapi/v1/shrimps/RegistrationLog/${globalSiteId}/${logId}`);
};

//#region STOCKING

//GET STOCKING
// /ewosapi/v1/shrimps/Stocking/{globalSiteId}/{logId}
export const getStocking = async (globalSiteId: string, logId: number, eventNr: number): Promise<Stocking> => {
  const res = await axios.get(`/ewosapi/v1/shrimps/Stocking/${globalSiteId}/${logId}/${eventNr}`);
  return res.data;
};

//GET NURSERY DATA
// /ewosapi/v1/shrimps/Stocking/nursery-data
export const getNursery = async (globalSiteId: string): Promise<NurseryList> => {
  const res = await axios.get(`/ewosapi/v1/shrimps/Stocking/nursery-data?globalSiteId=${globalSiteId}`);

  return res.data;
};

export const postStocking = async (globalSiteId: string, request: CreateStockingInput): Promise<Stocking> => {
  const res = await axios.post<Stocking>(`/ewosapi/v1/shrimps/Stocking/${globalSiteId}`, request);

  return res.data;
};

//#region TREATMENT
export const postTreatmentRegistration = async (globalSiteId: string, request: CreateTreatmentInput): Promise<Treatment> => {
  const res = await axios.post<Treatment>(`/ewosapi/v1/shrimps/TreatmentRegistration/${globalSiteId}`, request);
  return res.data;
};

export const getTreatmentRegistration = async (globalSiteId: string, logId: number, eventNr: number): Promise<Treatment> => {
  const res = await axios.get<Treatment>(`/ewosapi/v1/shrimps/TreatmentRegistration/${globalSiteId}/${logId}/${eventNr}`);
  return res.data;
};

export const putTreatmentRegistration = async (
  globalSiteId: string,
  logId: number,
  request: UpdateTreatmentInput
): Promise<AxiosResponse> => {
  return await axios.put<AxiosResponse>(`/ewosapi/v1/shrimps/TreatmentRegistration/${globalSiteId}/${logId}`, request);
};
// #endregion TREATMENT

// PUT
// /ewosapi/v1/shrimps/Stocking/{globalSiteId}/{logId}
export const putStocking = async (globalSiteId: string, logId: number, request: UpdateStockingInput): Promise<AxiosResponse> => {
  return await axios.put<AxiosResponse>(`/ewosapi/v1/shrimps/Stocking/${globalSiteId}/${logId}`, request);
};

//#endregion STOCKING

//#region FEEDING

// POST
export const postFeeding = async (globalSiteId: string, request: CreateFeedingInput): Promise<Feeding> => {
  const res = await axios.post<Feeding>(`/ewosapi/v1/shrimps/Feeding/${globalSiteId}`, request);

  return res.data;
};

// POST
// /ewosapi/v1/shrimps/ponds/{globalSiteId}/latest-feeding-status
export const postLatestFeedingStatus = async (globalSiteId: string, request: GetFeedingStatusInput): Promise<FeedingStatus> => {
  const res = await axios.post<FeedingStatus>(`/ewosapi/v1/shrimps/ponds/${globalSiteId}/latest-feeding-status?date=${request.date}`, {
    pondIds: request.pondIds,
  });
  return res.data;
};

// GET
// /ewosapi/v1/shrimps/Stocking/{globalSiteId}/{logId}/{eventNumber}
export const getFeeding = async (globalSiteId: string, logId: number, eventNr: number): Promise<Feeding> => {
  const res = await axios.get<Feeding>(`/ewosapi/v1/shrimps/Feeding/${globalSiteId}/${logId}/${eventNr}`);
  return res.data;
};

// PUT
// /ewosapi/v1/shrimps/Feeding/{globalSiteId}/{logId}
export const putFeeding = async (globalSiteId: string, logId: number, request: UpdateFeedingInput): Promise<AxiosResponse> => {
  return await axios.put<AxiosResponse>(`/ewosapi/v1/shrimps/Feeding/${globalSiteId}/${logId}`, request);
};

//#endregion FEEDING

//#region SAMPLING

// GET

// /ewosapi/v1/shrimps/Sampling/{globalSiteId}/{logId}/{eventNr}
export const getSampling = async (globalSiteId: string, logId: number, eventNr: number): Promise<Sampling> => {
  const res = await axios.get(`/ewosapi/v1/shrimps/Sampling/${globalSiteId}/${logId}/${eventNr}`);
  return res.data;
};

// PUT

// /ewosapi/v1/shrimps/Sampling/{globalSiteId}/{logId}
export const putSampling = async (globalSiteId: string, logId: number, request: UpdateSamplingInput): Promise<AxiosResponse> => {
  return await axios.put<AxiosResponse>(`/ewosapi/v1/shrimps/Sampling/${globalSiteId}/${logId}`, request);
};

// POST

// /ewosapi/v1/shrimps/Sampling/{globalSiteId}
export const postSampling = async (globalSiteId: string, request: CreateSamplingInput): Promise<Sampling> => {
  const res = await axios.post<Sampling>(`/ewosapi/v1/shrimps/Sampling/${globalSiteId}`, request);
  return res.data;
};

//#endregion SAMPLING

export const getFeeds = async (globalCompanyId: string): Promise<Feed[]> => {
  const res = await axios.get<Feed[]>(`/ewosapi/v1/shrimps/Feed/${globalCompanyId}`);

  return res.data;
};

//#region HARVEST

// GET

// /ewosapi/v1/shrimps/Harvest/{globalSiteId}/{logId}/{eventNr}
export const getHarvest = async (globalSiteId: string, logId: number, eventNr: number): Promise<Harvest> => {
  const res = await axios.get(`/ewosapi/v1/shrimps/Harvest/${globalSiteId}/${logId}/${eventNr}`);
  return res.data;
};

// POST

// /ewosapi/v1/shrimps/Harvest/{globalSiteId}
export const postHarvest = async (globalSiteId: string, request: CreateHarvestInput): Promise<Harvest> => {
  const res = await axios.post<Harvest>(`/ewosapi/v1/shrimps/Harvest/${globalSiteId}`, request);
  return res.data;
};

// PUT

// /ewosapi/v1/shrimps/Harvest/{globalSiteId}/{logId}
export const putHarvest = async (globalSiteId: string, logId: number, request: UpdateHarvest): Promise<AxiosResponse> => {
  const res = await axios.put<AxiosResponse>(`/ewosapi/v1/shrimps/Harvest/${globalSiteId}/${logId}`, request);
  return res.data;
};

//#endregion HARVEST

// /ewosapi/v1/shrimps/feeder/{globalCompanyId}/brands
export const getFeedersBrands = async (globalCompanyId: string): Promise<FeederBrand[]> => {
  const res = await axios.get<FeederBrand[]>(`/ewosapi/v1/shrimps/feeder/${globalCompanyId}/brands`);

  return res.data;
};

// #region TRANSFER

// POST
// /ewosapi/v1/shrimps/Transfer/{globalSiteId}
export const postTransfer = async (globalSiteId: string, data: CreateTransferInput): Promise<Transfer> => {
  const res = await axios.post<Transfer>(`/ewosapi/v1/shrimps/Transfer/${globalSiteId}`, data);
  return res.data;
};

// GET
// /ewosapi/v1/shrimps/Transfer/{globalSiteId}/{logId}/{eventNr}
export const getTransfer = async (globalSiteId: string, logId: number, eventNr: number): Promise<Transfer> => {
  const res = await axios.get<Transfer>(`/ewosapi/v1/shrimps/Transfer/${globalSiteId}/${logId}/${eventNr}`);
  return res.data;
};

// PUT
// /ewosapi/v1/shrimps/Transfer/{globalSiteId}
export const putTransfer = async (globalSiteId: string, data: UpdateTransferInput): Promise<Transfer> => {
  const res = await axios.put<Transfer>(`/ewosapi/v1/shrimps/Transfer/${globalSiteId}`, data);
  return res.data;
};

//GET QW
// /ewosapi​/v1​/shrimps​/WaterQuality​/{globalSiteId}​/{logId}​/{eventNr}

export const getWaterQuality = async (globalSiteId: string, logId: number, eventNr: number): Promise<WaterQuality> => {
  const res = await axios.get<WaterQuality>(`/ewosapi/v1/shrimps/WaterQuality/${globalSiteId}/${logId}/${eventNr}`);
  return res.data;
};

//POST WQ
// /ewosapi/v1/shrimps/WaterQuality/{globalSiteId}
export const postWaterQuality = async (globalSiteId: string, data: CreateWaterQualityInput): Promise<WaterQuality> => {
  const res = await axios.post<WaterQuality>(`/ewosapi/v1/shrimps/WaterQuality/${globalSiteId}`, data);
  return res.data;
};

//PUT WQ
// /ewosapi/v1/shrimps/WaterQuality/{globalSiteId}/{logId}
export const putWaterQuality = async (
  globalSiteId: string,
  logId: number,
  request: UpdateWaterQualityInput
): Promise<AxiosResponse> => {
  return await axios.put<AxiosResponse>(`/ewosapi/v1/shrimps/WaterQuality/${globalSiteId}/${logId}`, request);
};

// GET WQ TYPES
// /ewosapi/v1/shrimps/WaterQuality/{globalSiteId}/type

export const getWaterQualityType = async (globalSiteId: string): Promise<WaterQualityType[]> => {
  const res = await axios.get<WaterQualityType[]>(`/ewosapi/v1/shrimps/WaterQuality/${globalSiteId}/type`);
  return res.data;
};

// POST Get Registration Log Aerator
// /ewosapi/v1/shrimps/aerator/registration?globalSiteId=${globalSiteId}&logId=${logId}
export const getAeratorRegistrationLog = async (globalSiteId: string, logId: number) => {
  const res = await axios.get<AeratorRegistationLog>(
    `/ewosapi/v1/shrimps/aerator/registration?globalSiteId=${globalSiteId}&logId=${logId}`
  );
  return res.data;
};

// PUT RegistrationLogAerator
// /ewosapi/v1/shrimps/aerator/registration?globalSiteId=${globalSiteId}&logId=${logId}
export const putRegistrationLogAerator = async (globalSiteId: string, logId: number, request: UpdateAssignedAeratorsInput) => {
  const res = await axios.put<Aerator>(
    `/ewosapi/v1/shrimps/aerator/registration?globalSiteId=${globalSiteId}&logId=${logId}`,
    request
  );
  return res.data;
};

// GET Feeder Registration
// /ewosapi/v1/shrimps/feeder/registration?globalSiteId=${globalSiteId}&logId=${logId}
export const getFeederRegistrationLog = async (globalSiteId: string, logId: number) => {
  const res = await axios.get<FeederRegistationLog>(
    `/ewosapi/v1/shrimps/feeder/registration?globalSiteId=${globalSiteId}&logId=${logId}`
  );
  return res.data;
};

// PUT Feeder Registration
// /ewosapi/v1/shrimps/feeder/registration?globalSiteId=${globalSiteId}&logId=${logId}
export const putFeederRegistrationLog = async (
  globalSiteId: string,
  logId: number,
  request: UpdateAssignedFeedersInput
): Promise<AxiosResponse> => {
  return await axios.put<AxiosResponse>(
    `/ewosapi/v1/shrimps/feeder/registration?globalSiteId=${globalSiteId}&logId=${logId}`,
    request
  );
};

// #region GET PONDS FROM REGISTRATION

export const getFeedingPonds = (data: Feeding): string[] => {
  return data.cycles.map((c) => c.pondId);
};

export const getWaterQualityPonds = (data: WaterQuality): string[] => {
  return data.cycles.map((c) => c.pondId);
};

export const getStockingPonds = (data: Stocking): string[] => {
  return data.cycles.map((c) => c.pondId);
};

export const getTreatmentPonds = (data: Treatment): string[] => {
  return data.cycles.map((c) => c.pondId);
};

export const getTransferPonds = (data: Transfer): string[] => {
  return [data.fromCycle.pondId, ...data.toCycles.map((c) => c.pondId)];
};

export const getHarvestPonds = (data: Harvest): string[] => {
  return data.cycles.map((c) => c.pondId);
};

export const getSamplePonds = (data: Sampling): string[] => {
  return data.cycles.map((c) => c.pondId);
};

export const getAeratorPonds = (data: AeratorRegistationLog): string[] => {
  return data.pondAeratorAssignments.map((c) => c.pondId);
};

export const getFeederPonds = (data: FeederRegistationLog): string[] => {
  return data.pondFeederAssignments.map((c) => c.pondId);
};

export const registrationToPonds = async (globalSiteId: string, registration: Registration): Promise<string[]> => {
  switch (registration.actionType) {
    case RegistrationType.Feeding:
      return getFeedingPonds(await getFeeding(globalSiteId, registration.id, registration.eventNr));
    case RegistrationType.WaterQualitySample:
      return getWaterQualityPonds(await getWaterQuality(globalSiteId, registration.id, registration.eventNr));
    case RegistrationType.Stocking:
      return getStockingPonds(await getStocking(globalSiteId, registration.id, registration.eventNr));
    case RegistrationType.Harvest:
      return getHarvestPonds(await getHarvest(globalSiteId, registration.id, registration.eventNr));
    case RegistrationType.MeanWeightSample:
      return getSamplePonds(await getSampling(globalSiteId, registration.id, registration.eventNr));
    case RegistrationType.Transfer:
      return getTransferPonds(await getTransfer(globalSiteId, registration.id, registration.eventNr));
    case RegistrationType.Treatment:
      return getTreatmentPonds(await getTreatmentRegistration(globalSiteId, registration.id, registration.eventNr));
    case RegistrationType.Aerators:
      return getAeratorPonds(await getAeratorRegistrationLog(globalSiteId, registration.id));
    case RegistrationType.Feeders:
      return getFeederPonds(await getFeederRegistrationLog(globalSiteId, registration.id));
    default:
      return [];
  }
};

// #endregion GET PONDS FROM REGISTRATION
